import { get, set } from '../utils/storage'

export type Theme = 'light' | 'dark'
export type ThemePreference = Theme | 'system'

const STORAGE_KEY = 'theme'
const EVENT_NAME = 'sn:theme-change'

function systemTheme(): Theme {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyAttribute(preference: ThemePreference): void {
  if (preference === 'system') {
    document.documentElement.removeAttribute('data-theme')
  }
  else {
    document.documentElement.setAttribute('data-theme', preference)
  }
}

/** The theme preference the user (or the page) has explicitly chosen, or 'system' if none was set. */
export function getPreference(): ThemePreference {
  return get<ThemePreference>(STORAGE_KEY, 'system') ?? 'system'
}

/** The theme actually in effect right now (resolves 'system' to 'light'/'dark'). */
export function getResolved(): Theme {
  const pref = getPreference()
  return pref === 'system' ? systemTheme() : pref
}

/**
 * Sets the theme. Pass 'system' to follow the OS preference again.
 * Persists the choice (Section 19) and applies it immediately via the
 * `data-theme` attribute consumed by the design tokens (Section 9).
 */
export function setTheme(preference: ThemePreference): void {
  set(STORAGE_KEY, preference)
  applyAttribute(preference)
  document.dispatchEvent(new CustomEvent<Theme>(EVENT_NAME, { detail: getResolved() }))
}

/** Toggles between light and dark (ignores 'system'; starts from the resolved theme). */
export function toggle(): void {
  setTheme(getResolved() === 'dark' ? 'light' : 'dark')
}

/** Subscribes to theme changes (both explicit and, when following 'system', OS-level). Returns an unsubscribe function. */
export function onChange(callback: (theme: Theme) => void): () => void {
  const handler = (e: Event): void => callback((e as CustomEvent<Theme>).detail)
  document.addEventListener(EVENT_NAME, handler)
  return (): void => document.removeEventListener(EVENT_NAME, handler)
}

/** Called once by core/init: applies the persisted/system theme and reacts to OS-level changes while following 'system'. */
export function init(): void {
  applyAttribute(getPreference())

  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (getPreference() === 'system') {
      document.dispatchEvent(new CustomEvent<Theme>(EVENT_NAME, { detail: getResolved() }))
    }
  })
}
